<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const aboutData = ref(null);
const storyImage = ref("");
const chefImage = ref("");

const getImageSrc = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  let src = doc.querySelector("img")?.src || "";
  return src.replace(/-\d+x\d+(\.\w+)$/, "$1"); 
};

const fetchAboutData = async () => {
  try {
    const res = await fetch(
      "https://alwayshungryfoods.com/client-project/wp-json/wp/v2/aboutus",
      { cache: "force-cache" }
    );
    const data = await res.json();

    aboutData.value = data[0];

    storyImage.value = getImageSrc(
      aboutData.value.acf.ourstoryimages.simple_value_formatted
    );
    chefImage.value = getImageSrc(
      aboutData.value.acf.ourchefimage.simple_value_formatted
    );

  } catch (err) {
    console.error("API Error:", err);
  }
};

onMounted(fetchAboutData);
</script>

<template>
  <section class="about-section" v-if="aboutData">
    <div class="container">

      <div class="row align-center">

        <!-- IMAGE LEFT -->
        <div class="image-column col-lg-6 col-md-12 order-1 left">
          <div class="inner-column">
            <figure class="image-1">
              <img class="about-img" :src="storyImage" :alt="t('ourStoryAlt')" />
            </figure>
            <!-- <figure class="image-2 small-overlay">
              <img class="about-img" :src="chefImage" :alt="t('ourChefAlt')" />
            </figure> -->
          </div>
        </div>

        <!-- TEXT RIGHT -->
        <div class="content-column col-lg-6 col-md-12 order-2">
          <div class="inner-column">
            <div class="sec-title">
              <span class="title">{{ t('ourStoryTitle') }}</span>
              <h2>{{ t('ourStoryHeading') }}</h2>
              <div class="text" v-html="t('ourStoryDescription')"></div>
            </div>

            <ul class="list-style-one">
              <li>{{ t('traditionAuthenticity') }}</li>
              <li>{{ t('carefullyChosenIngredients') }}</li>
              <li>{{ t('familyPassion') }}</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  </section>

  <section v-else class="loading">
    <p>{{ t('loading') }}</p>
  </section>
</template>



<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=League+Gothic&display=swap');
body{
  font-family: 'Montserrat', sans-serif;
  font-family: 'League Gothic', sans-serif;
  font-family: 'Cormorant Garamond'sans-serif;
 }

img {
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
 
}

.about-img {
  /* width: clamp(280px, 70vw, 460px); */
  height: auto;
  object-fit: cover;
  box-shadow: 0 30px 50px rgba(8,13,62,.15);
  transition: 0.5s ease;
   aspect-ratio: 1/1;
}
ul { list-style: none; padding: 0; margin: 0; }

/* MAIN SECTION */
.about-section {
  position: relative;
  padding: 120px 0 70px;

}

.align-center {
  display: flex;
  align-items: center;
  padding: 5rem;
 

}
.row.align-center {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 2 columns: image + text */
  align-items: center;
  gap: 2rem;
  
}
/* TEXT COLUMN */
.content-column {
  display: flex;
  flex-direction: column;
}


.sec-title {
  margin-bottom: 30px;
}

.sec-title .title {
  font-size: 22px;
  font-weight: 700;
  color: #283A54;
  margin-bottom: 15px;
  font-family: 'Montserrat', sans-serif;
}

.sec-title h2 {
  font-size: 60px;
  font-weight: 300;
  position: relative;
  padding-bottom: 18px;
  font-family: 'Montserrat', sans-serif;
}

.sec-title h2:before {
  position: absolute;
  content: "";
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background: #d1d2d6;
}

.sec-title .text {
  margin-top: 5px;
  color: #5c5c5c;
  font-size: 26px;
  line-height: 36px;
  font-weight: 600;
   font-family: 'Cormorant Garamond', serif;
}

.list-style-one li {
  padding-left: 35px;
  font-size: 24px;
  margin-bottom: 10px;
  position: relative;
  font-family: 'Montserrat', sans-serif;
}

.list-style-one li::before {
  content: "\f058";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  color: #283A54;
  left: 0;
  position: absolute;
  
}

/* IMAGE COLUMN */
.image-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.image-column .inner-column {
  position: relative;
  padding-left: 120px;
 
}


.image-column figure {
  position: relative;
  z-index: 2;
}

.image-column .image-1 img,
.image-column .image-2 img {
  box-shadow: 0 30px 50px rgba(8,13,62,.15);
  
}


.image-column.left .inner-column::before {
  left: auto;
  right: -75px;  
}

.image-column.left .image-2 {
  left: auto;
  right: -15rem;
    bottom: 10rem; 
}

.small-overlay {
  width: 55%;
  bottom: 80px;
  position: absolute;
}
.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

/* When visible, add this class */
.fade-in.visible {
  opacity: 1;
  transform: translateY(0);
}
/* RESPONSIVE */
@media (max-width: 1400px) {
  .align-center {
  display: flex;
  align-items: center;
  padding: 2rem;
 

}
  .row.align-center {
    grid-template-columns: 1fr; /* 1 column stacked */
  }
  
}
@media (max-width: 750px) {
  .align-center {
  display: flex;
  align-items: center;
  padding: 2rem;
 

}
  .row.align-center {
    grid-template-columns: 1fr; /* 1 column stacked */
  }

  .image-column .inner-column {
    padding-left: 0;
    padding-bottom: 0;
  }

  .image-column .inner-column::before {
    display: none;
  }

  .image-2 {
    position: relative;
    width: 70% !important;
    margin: 20px auto 0;
  }
  .sec-title .text {
    font-size: 15px;
    line-height: 28px;
    padding: 2rem;
  }
  .sec-title h2 {
    font-size: 30px;
  }
 
  .list-style-one li {
    font-size: 13px;
  }
  .image-column.left .image-2 {
    right: -2rem;
     
  }
}
</style>
